import { Table } from "antd";
import { chain, find, flatten, get, isEmpty, sortBy, uniqBy } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoDuplicateOutline } from "react-icons/io5";
import {
  MdArrowBackIos,
  MdArrowForwardIos,
  MdOutlineExpandLess,
  MdOutlineExpandMore,
} from "react-icons/md";
import AlternativeMedicine from "shared/assets/images/ayurveda.png";
import Dental from "shared/assets/images/dental-checkup.png";
import Equipment from "shared/assets/images/hearing-aid.png";
import Hospitalization from "shared/assets/images/hospital-bed.png";
import Maternity from "shared/assets/images/maternity.png";
import MedicalCare from "shared/assets/images/medical-team.png";
import Others from "shared/assets/images/others.png";
import Prevention from "shared/assets/images/prevention.png";
import Optical from "shared/assets/images/vision.png";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Loading from "shared/components/Spin";
import { NAME_SPACES } from "shared/locales/constants";
import { COVERAGE_CATEGORIES } from "utils/constants";
import CoverageBenefitCard from "./Widgets/Coverages/Card";
import CoverageCategoryHeader from "./Widgets/Coverages/Header";
import CoverageLabel from "./Widgets/Coverages/Label";
import DownloadOrSend from "./Widgets/DownloadOrSend";
import InsurancePlanCard from "./Widgets/InsurancePlans";

export const COVERAGE_CATEGORIES_ICONS = {
  [COVERAGE_CATEGORIES.HOSPITALIZATION]: Hospitalization,
  [COVERAGE_CATEGORIES.MEDICAL_CARE]: MedicalCare,
  [COVERAGE_CATEGORIES.OPTICAL]: Optical,
  [COVERAGE_CATEGORIES.DENTAL]: Dental,
  [COVERAGE_CATEGORIES.EQUIPMENT]: Equipment,
  [COVERAGE_CATEGORIES.ALTERNATIVE_MEDICINE]: AlternativeMedicine,
  [COVERAGE_CATEGORIES.PREVENTION]: Prevention,
  [COVERAGE_CATEGORIES.MATERNITY]: Maternity,
  [COVERAGE_CATEGORIES.OTHER]: Others,
};

const LABEL_COLUMN_WIDTH = 300;
const MIN_PLAN_COLUMN_WIDTH = 220;

const getPlanColumnWidth = (containerWidth, planCount) => {
  if (!planCount) return MIN_PLAN_COLUMN_WIDTH;

  const availableWidth = Math.max(0, containerWidth - LABEL_COLUMN_WIDTH);
  const sharedWidth =
    containerWidth > 0 ? availableWidth / planCount : MIN_PLAN_COLUMN_WIDTH;

  return Math.max(MIN_PLAN_COLUMN_WIDTH, sharedWidth);
};

// The same benefit can exist several times in the catalogue (identical labels,
// distinct ids), and a single id can be attached to two categories. Rows are
// built from category + label, so values must be resolved the same way:
// matching on the id would tie the display to whichever plan comes first.
const getCoverageBenefit = (
  insurancePlanCoverages,
  categoryValue,
  coverageType,
) => {
  const coverageMappings = flatten(
    insurancePlanCoverages
      .filter(
        ({ coverageCategory }) =>
          get(coverageCategory, "value") === categoryValue,
      )
      .map(({ coverageMappings: mappings }) => mappings || []),
  );
  const coverageMapping = find(
    coverageMappings,
    ({ coverage }) => get(coverage, "type") === coverageType,
  );
  if (!coverageMapping) return undefined;
  const { coverage, limits, supportingInformation } = coverageMapping;
  return { ...coverage, limits, supportingInformation };
};

const View = ({
  project,
  onSubscribe,
  onDownload,
  onSendQuote,
  data,
  onBack,
  loading,
  openModal,
  setOpenModal,
  duplicateProject,
  hideDetail,
  setHideDetail,
}) => {
  const { locked, fields, subscriptions, contract } = project;
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const toggleCoverage = (name, hide) => {
    setHideDetail((prev) => ({
      ...prev,
      [name]: hide,
    }));
  };

  const HIDE_DETAIL = {
    [false]: (
      <>
        <span>{DETAIL.TEMPLATES.COMPARISON.SEE_MORE}</span>
        <MdOutlineExpandMore />
      </>
    ),
    [true]: (
      <>
        <span>{DETAIL.TEMPLATES.COMPARISON.SEE_LESS}</span>
        <MdOutlineExpandLess />
      </>
    ),
  };

  const dataSource = useMemo(() => {
    if (isEmpty(data)) return;
    const groupedCoverageBenefits = chain(data)
      .flatMap((cost) => get(cost, "insurancePlan.insurancePlanCoverages"))
      .value();

    const mergedCoverages = uniqBy(
      groupedCoverageBenefits,
      (coverage) => coverage.coverageCategory.value,
    );

    const coveragesByCategory = mergedCoverages.map(
      ({ coverageMappings, ...coverage }) => {
        const matchingCoverages = groupedCoverageBenefits.filter(
          (item) =>
            item.coverageCategory.value === coverage.coverageCategory.value,
        );

        return {
          ...coverage,
          coverages: uniqBy(
            flatten(matchingCoverages.map((c) => c.coverageMappings)),
            "coverage.id",
          ),
        };
      },
    );
    const res = sortBy(coveragesByCategory, "coverageCategory.order").flatMap(
      ({ coverageCategory, coverages }) => {
        const categoryBenefits = [
          coverageCategory,
          ...uniqBy(
            sortBy(
              coverages.flatMap(({ coverage }) => ({
                ...coverage,
                categoryValue: coverageCategory.value,
                rowId: coverageCategory.value + coverage.id,
              })),
              "order",
            ),
            "id",
          ),
        ];
        const uniqueBenefits = categoryBenefits.reduce((acc, benefit) => {
          if (!benefit.type) acc.push(benefit);
          const existing = acc.find((item) => item.type === benefit.type);
          if (!existing) {
            acc.push(benefit);
          }
          return acc;
        }, []);
        return uniqueBenefits;
      },
    );

    return res;
  }, [loading]);
  const tableContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const node = tableContainerRef.current;
    if (!node) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [loading]);

  const planCount = data?.length || 0;
  const planColumnWidth = getPlanColumnWidth(containerWidth, planCount);
  const tableScrollX = LABEL_COLUMN_WIDTH + planCount * planColumnWidth;

  const columns = [
    {
      width: LABEL_COLUMN_WIDTH,
      dataIndex: "__typename",
      render: (record, row) => {
        const isCoverageHeader = record === "CoverageCategory";
        return isCoverageHeader ? (
          <CoverageCategoryHeader
            className="coverage-header"
            src={get(row, "icon")}
            type={row.label}
            label={row.label}
          />
        ) : (
          <CoverageLabel
            row={row}
            label={row.type}
            subLabel={get(row, "subtype", "")}
            info={get(row, "additionalInfo", "")}
            expanded={hideDetail[row.id]}
            link={HIDE_DETAIL[get(hideDetail, row.id, false)]}
            onToggle={() => toggleCoverage(row.id, !hideDetail[row.id])}
          />
        );
      },
      // fixed: true,
    },
    ...(data?.map(({ insurancePlan, cost, currency }) => ({
      width: planColumnWidth,
      dataIndex: "type",
      title: () => (
        <InsurancePlanCard
          insurancePlan={insurancePlan}
          subscriptions={subscriptions}
          issuanceDate={get(contract, "issuanceDate")}
          isRIA={get(project, "ria", false)}
          cost={cost}
          currency={currency}
          translation={DETAIL.TEMPLATES.COMPARISON}
          onSubscribe={onSubscribe}
          expired={project.expired}
          locked={project.locked}
        />
      ),
      render: (record, row) => {
        return record ? (
          <CoverageBenefitCard
            translation={DETAIL.TEMPLATES.COMPARISON}
            expanded={hideDetail[row.type]}
            coverageBenefit={getCoverageBenefit(
              get(insurancePlan, "insurancePlanCoverages", []),
              row.categoryValue,
              row.type,
            )}
          />
        ) : null;
      },
    })) || []),
  ];

  return (
    <div>
      <Header
        key={"header"}
        title={DETAIL.TEMPLATES.COMPARISON.TITLE}
        actions={[]}
      />
      <div className="comparison-container">
        <div className="d-flex justify--end">
          {locked && project?.expired ? (
            <>
              <Button
                onClick={() => duplicateProject()}
                type="primary--outlined"
              >
                <IoDuplicateOutline size={16} />
                {DETAIL.TEMPLATES.COMPARISON.DUPLICATE_PROJECT}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setOpenModal(true)}
              type="primary--outlined"
              disabled={project?.expired}
            >
              {DETAIL.TEMPLATES.COMPARISON.NEXT}
              <MdArrowForwardIos size={16} />
            </Button>
          )}
        </div>
        <Card type="simple">
          <DownloadOrSend
            data={data}
            open={openModal}
            onClose={() => setOpenModal(false)}
            onDownload={onDownload}
            onSendQuote={onSendQuote}
            fields={fields}
            insurancePlans={get(project, "subscriptions", []).map(
              ({ insurancePlan }) => insurancePlan?.id,
            )}
          />
          {loading ? (
            <Loading />
          ) : (
            <div ref={tableContainerRef}>
              <Table
                dataSource={dataSource}
                columns={columns}
                className="comparison-table__wrapper"
                rowKey={(record) => get(record, "rowId", record.id)}
                pagination={false}
                tableLayout="fixed"
                scroll={{ x: tableScrollX, y: 520 }}
                rowClassName={(record) =>
                  record.__typename === "CoverageCategory"
                    ? "coverage-header-row"
                    : ""
                }
              />
            </div>
          )}
        </Card>

        <Button
          onClick={onBack}
          type="secondary--link"
          disabled={project.locked}
        >
          <MdArrowBackIos size={16} />
          {DETAIL.TEMPLATES.COMPARISON.PREVIOUS}
        </Button>
      </div>
    </div>
  );
};

export default View;
